import { BadgeVariant } from '../../../../components/Badge';
import { StepStatus } from '../StatusStepIndicator';

/**
 * eNOVA workflow phase model (ACP-1040).
 *
 * Steps 1-5 and 8 are SEQUENTIAL and mutually exclusive — the application sits in
 * exactly one of them at a time, captured by the single `applicationStatus` field.
 * Their tracker state is derived here from that field.
 *
 * The fingerprint + eApp steps (PHASE.vetting) are PARALLEL and progress
 * independently, so they are NOT driven by `applicationStatus` — they read the
 * per-region `applicationSubStatus` object instead (see buildTrackerSteps).
 */
export const PHASE = {
  foreignNational: 3,
  onboarding: 0,
  prescreening: 6,
  security: 4,
  sponsor: 2,
  submit: 1,
  vetting: 5,
} as const;

export type StepStateName = 'complete' | 'current' | 'attention' | 'pending';

/**
 * One visual treatment per state. `attention` is the design's red "Action Needed"
 * (returned-for-updates / denied / timed out / system error): red retry icon + red badge.
 */
export const STATE_DISPLAY: Record<StepStateName, { stepStatus: StepStatus; badgeVariant?: BadgeVariant }> = {
  attention: { badgeVariant: 'action-needed', stepStatus: 'error' },
  complete: { badgeVariant: 'complete', stepStatus: 'completed' },
  current: { badgeVariant: 'current', stepStatus: 'current' },
  pending: { badgeVariant: undefined, stepStatus: 'pending' },
};

export interface PhasePosition {
  /** Phase the application currently sits in. */
  phase: number;
  /** The current phase needs attention (returned for updates, denied, timed out, errored). */
  attention?: boolean;
  /** Terminal success — every step is complete regardless of phase. */
  allComplete?: boolean;
}

/**
 * applicationStatus → position in the tracker. Mirrors APPLICATION_STATUS in
 * @access-cms/db-core; redeclared as string keys because the UI does not depend
 * on db-core.
 *
 * Terminal outcomes are supplied separately by the application-scoped
 * `trackerState` projection because the terminal status alone cannot identify
 * which reviewer rejected or cancelled the application. Unknown statuses still
 * fall through to `undefined` and render neutral.
 */
// Ordered by workflow phase (not alphabetically) so the progression reads top to
// bottom; this is a keyed lookup, so key order is functionally irrelevant.
/* eslint-disable sort-keys-fix/sort-keys-fix */
const STATUS_POSITION: Record<string, PhasePosition> = {
  // A draft sits at Submit Application: Onboarding is complete, Submit is the current step.
  DRAFT: { phase: PHASE.submit },

  SUBMITTED: { phase: PHASE.sponsor },
  COR_REVIEW: { phase: PHASE.sponsor },
  UNDER_REVIEW: { phase: PHASE.sponsor },
  RETURNED: { attention: true, phase: PHASE.submit },

  COR_APPROVED: { phase: PHASE.foreignNational },
  FNMS_REVIEW: { phase: PHASE.foreignNational },
  FNMS_RETURNED: { attention: true, phase: PHASE.foreignNational },
  // ACP-2421: both FNMS failure states, mirroring how the VAS ones are treated below.
  // FNMS_ERROR was missing entirely, so an application that died on submission rendered
  // an all-neutral tracker — the applicant saw nothing at all where VAS_ERROR would have
  // shown attention. FNMS_SUBMISSION_FAILED is its recoverable counterpart.
  FNMS_ERROR: { attention: true, phase: PHASE.foreignNational },
  FNMS_SUBMISSION_FAILED: { attention: true, phase: PHASE.foreignNational },

  FNMS_APPROVED: { phase: PHASE.security },
  VAS_REVIEW: { phase: PHASE.security },
  VAS_RETURNED: { attention: true, phase: PHASE.security },
  VAS_ERROR: { attention: true, phase: PHASE.security },
  VAS_TIMEOUT: { attention: true, phase: PHASE.security },

  VAS_APPROVED: { phase: PHASE.vetting },
  VAS_AWAITING_FINGERPRINTS_OR_EAPP: { phase: PHASE.vetting },
  VAS_FINGERPRINTS_REQUESTED: { phase: PHASE.vetting },
  VAS_FINGERPRINTS_COMPLETED: { phase: PHASE.vetting },
  VAS_FINGERPRINTS_NOT_REQUIRED: { phase: PHASE.vetting },
  VAS_FINGERPRINTS_TIMEOUT: { phase: PHASE.vetting },
  VAS_EAPP_REQUESTED: { phase: PHASE.vetting },
  VAS_EAPP_COMPLETED: { phase: PHASE.vetting },
  VAS_EAPP_NOT_REQUIRED: { phase: PHASE.vetting },
  VAS_EAPP_TIMEOUT: { phase: PHASE.vetting },

  VAS_AWAITING_LOGICAL_ACCESS: { phase: PHASE.prescreening },
  VAS_LOGICAL_ACCESS_GRANTED: { allComplete: true, phase: PHASE.prescreening },
  LOGICAL_ACCESS_GRANTED: { allComplete: true, phase: PHASE.prescreening },
  VAS_LOGICAL_ACCESS_DENIED: { attention: true, phase: PHASE.prescreening },
  LOGICAL_ACCESS_DENIED: { attention: true, phase: PHASE.prescreening },

  APPROVED: { allComplete: true, phase: PHASE.prescreening + 1 },
};
/* eslint-enable sort-keys-fix/sort-keys-fix */

/** Resolve an applicationStatus to its tracker position, or undefined if unknown. */
export function getPhasePosition(applicationStatus?: string): PhasePosition | undefined {
  return applicationStatus ? STATUS_POSITION[applicationStatus] : undefined;
}

/**
 * Derive the tracker state for a SEQUENTIAL step (steps 1-5, 8) from the
 * application's phase position. Past phases are complete, the current phase is
 * current (or attention), future phases are pending. An unknown position keeps
 * everything neutral/pending.
 */
export function deriveSequentialStepState(stepPhase: number, pos: PhasePosition | undefined): StepStateName {
  if (!pos) return 'pending';
  if (pos.allComplete) return 'complete';
  if (stepPhase < pos.phase) return 'complete';
  if (stepPhase === pos.phase) return pos.attention ? 'attention' : 'current';

  return 'pending';
}
